import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, RefreshCw, Search, LogOut } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "gls_admin_token";
const authHeaders = (token) => ({ headers: { "X-Admin-Token": token } });

export default function AdminLeads() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [authed, setAuthed] = useState(false);
  const [signups, setSignups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");

  const login = async () => {
    if (!token.trim()) return toast.error("Enter your admin token");
    try {
      await axios.get(`${API}/admin/newsletter`, authHeaders(token.trim()));
      localStorage.setItem(TOKEN_KEY, token.trim());
      setAuthed(true);
      load(token.trim());
    } catch {
      toast.error("Invalid admin token");
    }
  };

  const load = async (t) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/newsletter`, authHeaders(t || token));
      setSignups(r.data.signups || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      axios
        .get(`${API}/admin/newsletter`, authHeaders(token))
        .then((r) => { setAuthed(true); setSignups(r.data.signups || []); })
        .catch(() => setAuthed(false));
    }
  }, []); // eslint-disable-line

  const filtered = useMemo(() => {
    return signups.filter((s) => {
      if (sourceFilter !== "all" && (s.source || "site") !== sourceFilter) return false;
      if (!q) return true;
      const hay = `${s.email || ""} ${s.name || ""}`.toLowerCase();
      return hay.includes(q.toLowerCase());
    });
  }, [signups, q, sourceFilter]);

  const sources = useMemo(() => {
    const set = new Set(signups.map((s) => s.source || "site"));
    return ["all", ...Array.from(set).sort()];
  }, [signups]);

  const downloadCsv = () => {
    const url = `${API}/admin/newsletter.csv`;
    // fetch as blob so we can pass the admin token header, then trigger a download
    fetch(url, { headers: { "X-Admin-Token": token } })
      .then((r) => {
        if (!r.ok) throw new Error("Download failed");
        return r.blob();
      })
      .then((blob) => {
        const a = document.createElement("a");
        const objUrl = URL.createObjectURL(blob);
        a.href = objUrl;
        a.download = `newsletter_signups_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objUrl);
      })
      .catch(() => toast.error("Could not download CSV"));
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setSignups([]);
  };

  if (!authed) {
    return (
      <div className="max-w-md mx-auto py-24 px-6" data-testid="admin-leads-login">
        <h1 className="font-serif text-3xl text-[#2D2628] mb-6 text-center">Admin — Leads</h1>
        <input
          type="password"
          placeholder="Admin token"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          className="w-full px-4 py-3 border border-[#E8DCDD] rounded-sm mb-4 focus:outline-none focus:border-[#8C4A57]"
          data-testid="admin-leads-token-input"
        />
        <button
          onClick={login}
          className="w-full bg-[#8C4A57] text-white py-3 rounded-sm hover:bg-[#6f3945] transition"
          data-testid="admin-leads-login-btn"
        >
          Sign in
        </button>
        <p className="text-xs text-[#6B5E62] mt-4 text-center">
          <Link to="/admin/uploads" className="underline">Back to admin</Link>
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto py-12 px-6" data-testid="admin-leads-page">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-3xl text-[#2D2628]">Captured leads</h1>
          <p className="text-sm text-[#6B5E62]">
            All email opt-ins from the P.A.C.T. Framework guide, exit-intent modal,
            /character landing page, and course-page offers.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/admin/uploads"
            className="text-xs px-3 py-2 border border-[#E8DCDD] rounded-sm hover:border-[#8C4A57] transition text-[#4A3E42]"
          >
            Admin console
          </Link>
          <button
            onClick={() => load()}
            disabled={loading}
            data-testid="admin-leads-refresh"
            className="flex items-center gap-1.5 text-xs px-3 py-2 border border-[#E8DCDD] rounded-sm hover:border-[#8C4A57] transition text-[#4A3E42]"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            onClick={downloadCsv}
            data-testid="admin-leads-export-csv"
            className="flex items-center gap-1.5 text-xs px-3 py-2 bg-[#8C4A57] text-white rounded-sm hover:bg-[#6f3945] transition font-semibold uppercase tracking-wider"
          >
            <Download className="w-3.5 h-3.5" />
            Export CSV
          </button>
          <button
            onClick={logout}
            className="flex items-center gap-1.5 text-xs px-3 py-2 text-[#6B5E62] hover:text-[#2D2628] transition"
          >
            <LogOut className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-4 h-4 text-[#6B5E62] absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            placeholder="Search by email or name"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="admin-leads-search"
            className="w-full pl-9 pr-3 py-2.5 border border-[#E8DCDD] rounded-sm text-sm focus:outline-none focus:border-[#8C4A57]"
          />
        </div>
        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          data-testid="admin-leads-source-filter"
          className="px-3 py-2.5 border border-[#E8DCDD] rounded-sm text-sm bg-white focus:outline-none focus:border-[#8C4A57]"
        >
          {sources.map((s) => (
            <option key={s} value={s}>{s === "all" ? "All sources" : s}</option>
          ))}
        </select>
        <span className="text-xs text-[#6B5E62]" data-testid="admin-leads-count">
          {filtered.length} of {signups.length}
        </span>
      </div>

      <div className="border border-[#E8DCDD] rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#FAF4F5] text-[10px] uppercase tracking-wider text-[#6B5E62]">
            <tr>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Source</th>
              <th className="text-left px-4 py-3">Created</th>
              <th className="text-left px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center py-10 text-[#6B5E62]">
                  {loading ? "Loading…" : "No leads match your filters."}
                </td>
              </tr>
            )}
            {filtered.map((s, i) => (
              <tr
                key={s.email + i}
                data-testid={`admin-lead-row-${i}`}
                className="border-t border-[#E8DCDD] hover:bg-[#FAF4F5]"
              >
                <td className="px-4 py-3 text-[#2D2628]">{s.email}</td>
                <td className="px-4 py-3 text-[#4A3E42]">{s.name || "—"}</td>
                <td className="px-4 py-3 text-[#4A3E42]">{s.source || "site"}</td>
                <td className="px-4 py-3 text-[#6B5E62] whitespace-nowrap">
                  {s.created_at ? new Date(s.created_at).toLocaleString() : "—"}
                </td>
                <td className="px-4 py-3">
                  {s.unsubscribed_at ? (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#F7E2E5] text-[#8C4A57]">
                      Unsubscribed
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#E8F5EA] text-[#3F7A4F]">
                      Subscribed
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
